import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, EmptyState, ErrorState, Skeleton, TextField, Pill } from "../../components/ui.tsx";
import { ConfirmDialog, Modal } from "../../components/dialog.tsx";
import { get, patch, post, applyFieldErrors, type ApiError } from "../../lib/api.ts";
import { planKey, usePlan } from "../../lib/plan.tsx";
import type { Unit } from "../../lib/types.ts";
import { requiredText } from "../../../shared/setup.ts";
import { LIMITS } from "../../../shared/limits.ts";
import { useCommand } from "./shared.tsx";
import { useUrlState } from "../../lib/nav.tsx";
import { ArchivedToggle } from "./shared.tsx";

const unitForm = z.object({ name: requiredText(LIMITS.name, "Informe o nome da unidade.") });

export function UnitsTab() {
  const plan = usePlan();
  const [view, setView] = useUrlState({ arquivados: "" });
  const showArchived = view.arquivados === "1";
  const units = useQuery({
    queryKey: planKey(plan.id, "units", showArchived ? "with-archived" : "active"),
    queryFn: async () => (await get<{ data: Unit[] }>(`/plans/${plan.id}/units${showArchived ? "?include_archived=true" : ""}`)).data,
  });
  const [editing, setEditing] = useState<Unit | "new" | null>(null);
  const [archiving, setArchiving] = useState<Unit | null>(null);
  const { run, busy } = useCommand(plan.id);

  if (units.isPending) return <Skeleton lines={4} />;
  if (units.isError) return <ErrorState error={units.error} onRetry={() => void units.refetch()} />;
  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted">Unidades institucionais responsáveis pelas iniciativas. Não são perfis de acesso.</p>
        <div className="flex flex-wrap items-center gap-3"><ArchivedToggle checked={showArchived} onChange={(v) => setView({ arquivados: v ? "1" : "" })} /><Button onClick={() => setEditing("new")}>Nova unidade</Button></div>
      </div>
      {units.data.length === 0 ? (
        <EmptyState title="Ainda não há unidades responsáveis." action={<Button onClick={() => setEditing("new")}>Cadastrar unidade</Button>}>
          Uma iniciativa só pode ser submetida com uma unidade responsável ativa.
        </EmptyState>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-panel border border-line bg-surface shadow-e1">
          {units.data.map((u) => (
            <li key={u.id} className={`flex flex-wrap items-center gap-3 px-4 py-3 ${u.archived_at ? "opacity-70" : ""}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{u.name} {u.archived_at && <Pill tone="gray">Arquivada</Pill>}</p>
                <p className="text-xs text-muted">{u.initiative_count} iniciativa(s)</p>
              </div>
              {!u.archived_at && (
                <>
                  <Button variant="secondary" onClick={() => setEditing(u)}>Renomear</Button>
                  <Button variant="dangerGhost" onClick={() => setArchiving(u)}>Arquivar</Button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {editing && <UnitDialog unit={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog open={archiving !== null} onOpenChange={(o) => !o && setArchiving(null)} title="Arquivar unidade?" confirmLabel="Arquivar" danger loading={busy}
        onConfirm={async () => {
          await run(() => post(`/plans/${plan.id}/units/${archiving!.id}/archive`, { expected_version: archiving!.version }), "Unidade arquivada.");
          setArchiving(null);
        }}>
        <p className="text-sm">Iniciativas que já indicam “{archiving?.name}” continuam mostrando o nome, mas precisarão de uma unidade ativa para nova submissão.</p>
      </ConfirmDialog>
    </>
  );
}

function UnitDialog({ unit, onClose }: { unit: Unit | null; onClose: () => void }) {
  const plan = usePlan();
  const { run } = useCommand(plan.id);
  const form = useForm<z.input<typeof unitForm>>({ resolver: zodResolver(unitForm), defaultValues: { name: unit?.name ?? "" } });
  const submit = form.handleSubmit(async (v) => {
    const ok = await run(
      () => (unit ? patch(`/plans/${plan.id}/units/${unit.id}`, { ...v, expected_version: unit.version }) : post(`/plans/${plan.id}/units`, v)),
      unit ? "Unidade atualizada." : "Unidade cadastrada.",
      (e: ApiError) => {
        applyFieldErrors(e, form.setError as never);
        return e.fieldErrors.length > 0;
      },
    );
    if (ok) onClose();
  });
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={unit ? "Renomear unidade" : "Nova unidade"}
      footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button onClick={() => void submit()} loading={form.formState.isSubmitting}>Salvar</Button></>}>
      <form noValidate onSubmit={submit}>
        <TextField label="Nome da unidade" required autoFocus error={form.formState.errors.name?.message} {...form.register("name")} />
        <button type="submit" className="hidden" />
      </form>
    </Modal>
  );
}
